import { idbClear, idbDelete, idbGetAll, idbPut } from "./idb.js";

const STORAGE_KEY = "portwatch-agent.datasets";

function resolveStorage(storage) {
  if (storage) return storage;
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function dateRange(records) {
  const dates = records.map((r) => r.date).filter(Boolean).sort();
  return { from: dates[0] ?? null, to: dates.at(-1) ?? null };
}

function summarizeDataset(dataset) {
  return {
    id: dataset.id,
    portid: dataset.portid,
    scope: dataset.scope,
    title: dataset.title,
    recordCount: dataset.records.length,
    columns: dataset.columns,
    dateRange: dataset.dateRange ?? dateRange(dataset.records),
    createdAt: dataset.createdAt,
  };
}

function sequenceFromId(id) {
  const match = /^dataset_(\d+)$/.exec(id ?? "");
  return match ? Number(match[1]) : 0;
}

/**
 * 取得した時系列データのブラウザ内ストア。生レコードは会話履歴へ入れず、
 * datasetId を介して後続ツールから参照する。要約は localStorage、
 * 生レコードは IndexedDB へ二段で永続化する。
 */
export class DatasetStore {
  #datasets = new Map();
  #summaries = new Map();
  #listeners = new Set();
  #sequence = 0;
  #storage;

  constructor({ storage } = {}) {
    this.#storage = resolveStorage(storage);
    this.#restore();
    this.#hydrate();
  }

  add(dataset) {
    this.#sequence += 1;
    const id = `dataset_${String(this.#sequence).padStart(3, "0")}`;
    const stored = {
      ...dataset,
      id,
      dateRange: dataset.dateRange ?? dateRange(dataset.records),
      createdAt: new Date().toISOString(),
    };

    this.#datasets.set(id, stored);
    this.#summaries.set(id, summarizeDataset(stored));
    this.#persist();
    idbPut(stored).catch(() => {});
    this.#notify();
    return stored;
  }

  get(id) {
    const dataset = this.#datasets.get(id);
    if (!dataset) {
      throw new Error(`データセットが見つかりません: ${id}`);
    }
    return dataset;
  }

  list() {
    return [...this.#summaries.values()].map((summary) => ({
      ...summary,
      available: this.#datasets.has(summary.id),
    }));
  }

  inspect(id, { sampleSize = 5, distinctColumn } = {}) {
    const dataset = this.get(id);
    const result = {
      ...summarizeDataset(dataset),
      sample: dataset.records.slice(0, sampleSize),
    };

    if (distinctColumn) {
      if (!dataset.columns.includes(distinctColumn)) {
        throw new Error(`列が見つかりません: ${distinctColumn}`);
      }
      result.distinct = [
        ...new Set(dataset.records.map((record) => record[distinctColumn])),
      ].slice(0, 100);
    }

    return result;
  }

  clear() {
    this.#datasets.clear();
    this.#summaries.clear();
    this.#sequence = 0;
    try {
      this.#storage?.removeItem(STORAGE_KEY);
    } catch {
      // 削除失敗は無視する。
    }
    idbClear().catch(() => {});
    this.#notify();
  }

  remove(id) {
    const hadData = this.#datasets.delete(id);
    const hadSummary = this.#summaries.delete(id);
    if (!hadData && !hadSummary) return;
    this.#persist();
    idbDelete(id).catch(() => {});
    this.#notify();
  }

  subscribe(listener) {
    this.#listeners.add(listener);
    listener(this.list());
    return () => this.#listeners.delete(listener);
  }

  #restore() {
    let summaries;
    try {
      const raw = this.#storage?.getItem(STORAGE_KEY);
      summaries = raw ? JSON.parse(raw) : [];
    } catch {
      summaries = [];
    }
    if (!Array.isArray(summaries)) return;

    for (const summary of summaries) {
      if (!summary?.id) continue;
      this.#summaries.set(summary.id, summary);
      this.#sequence = Math.max(this.#sequence, sequenceFromId(summary.id));
    }
  }

  async #hydrate() {
    let datasets;
    try {
      datasets = await idbGetAll();
    } catch {
      return;
    }
    if (!Array.isArray(datasets) || datasets.length === 0) return;

    for (const dataset of datasets) {
      if (!dataset?.id) continue;
      this.#datasets.set(dataset.id, dataset);
      if (!this.#summaries.has(dataset.id)) {
        this.#summaries.set(dataset.id, summarizeDataset(dataset));
      }
      this.#sequence = Math.max(this.#sequence, sequenceFromId(dataset.id));
    }
    this.#notify();
  }

  #persist() {
    try {
      this.#storage?.setItem(
        STORAGE_KEY,
        JSON.stringify([...this.#summaries.values()]),
      );
    } catch {
      // quota超過などで保存に失敗してもメモリ保持は継続する。
    }
  }

  #notify() {
    const snapshot = this.list();
    for (const listener of this.#listeners) listener(snapshot);
  }
}
